import React, { useState, useCallback, useContext, createContext } from 'react';
import { AlertTriangle, Info, CheckCircle, X } from 'lucide-react';

const ModalContext = createContext(null);

export function useModal() {
  return useContext(ModalContext);
}

export function ModalProvider({ children }) {
  const [modal, setModal] = useState(null);

  const showAlert = useCallback((message, { title, type = 'info' } = {}) => {
    return new Promise((resolve) => {
      setModal({ type: 'alert', message, title, variant: type, resolve });
    });
  }, []);

  const showConfirm = useCallback((message, { title, confirmText = 'OK', cancelText = 'Abbrechen', type = 'warning' } = {}) => {
    return new Promise((resolve) => {
      setModal({ type: 'confirm', message, title, confirmText, cancelText, variant: type, resolve });
    });
  }, []);

  const close = (result) => {
    if (modal?.resolve) modal.resolve(result);
    setModal(null);
  };

  const Icon = modal?.variant === 'warning' ? AlertTriangle
    : modal?.variant === 'success' ? CheckCircle : Info;
  const iconColor = modal?.variant === 'warning' ? 'text-amber-400'
    : modal?.variant === 'success' ? 'text-green-400' : 'text-blue-400';

  return (
    <ModalContext.Provider value={{ alert: showAlert, confirm: showConfirm }}>
      {children}
      {modal && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="custom-modal-overlay">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => close(modal.type === 'confirm' ? false : undefined)} />
          <div className="relative bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl max-w-sm w-full p-5 animate-in fade-in zoom-in-95 duration-200" data-testid="custom-modal">
            <button onClick={() => close(modal.type === 'confirm' ? false : undefined)} className="absolute top-3 right-3 text-zinc-500 hover:text-zinc-300">
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3">
              <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${iconColor}`} />
              <div className="flex-1 min-w-0">
                {modal.title && <h3 className="font-semibold text-zinc-100 text-sm mb-1">{modal.title}</h3>}
                <p className="text-zinc-300 text-sm leading-relaxed">{modal.message}</p>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              {modal.type === 'confirm' && (
                <button
                  onClick={() => close(false)}
                  className="px-3 py-1.5 text-xs font-medium text-zinc-400 bg-zinc-800 border border-zinc-700 rounded-lg hover:bg-zinc-700 transition-colors"
                  data-testid="modal-cancel-btn"
                >
                  {modal.cancelText}
                </button>
              )}
              <button
                onClick={() => close(modal.type === 'confirm' ? true : undefined)}
                className="px-3 py-1.5 text-xs font-medium text-zinc-100 bg-amber-600 border border-amber-500 rounded-lg hover:bg-amber-500 transition-colors"
                data-testid="modal-confirm-btn"
              >
                {modal.type === 'confirm' ? modal.confirmText : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </ModalContext.Provider>
  );
}
